import { useSyncExternalStore } from "react";
import {
  reactToOccurrence,
  removeOccurrenceReaction,
  setOccurrenceLike,
} from "../api/OccurrenceEngagementAPI";

// In-session engagement summaries of event occurrences, by occurrence ID —
// the one copy Home's event-start cards and the occurrence preview both
// render, so a like or comment in one shows in the other. Same module-level
// pattern as useCommentsStore.
//
// An entry is { engagement, revoked, seq }:
//   engagement  the backend summary ({ can_engage, like_count, is_liked,
//               reaction_counts, my_reaction, comment_count }), or null when
//               engagement isn't open (private, not started)
//   revoked     the viewer lost access (the preview answered 404): nothing
//               cached is shown, and Home hides the card
//   seq         when it was last written (see below)
//
// Server snapshots (a Home page, a preview) are applied with `seed`, passing
// the `mark()` taken before their request: a snapshot is dropped when the
// entry was written after that request began, so a slow or older page never
// overwrites a newer like, reaction, comment count or revocation.

let entries = {};
let pendingLikes = new Set();
let pendingReactions = new Set();
let seq = 0;
let sessionToken = null;
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function write(id, fields) {
  seq += 1;
  const current = entries[id] || { engagement: null, revoked: false };
  entries = { ...entries, [id]: { ...current, ...fields, seq } };
  emit();
}

function patchEngagement(id, fields) {
  const current = entries[id];
  if (!current?.engagement || current.revoked) return;
  write(id, { engagement: { ...current.engagement, ...fields } });
}

function setPending(set, id, present) {
  const next = new Set(set);
  if (present) next.add(id);
  else next.delete(id);
  return next;
}

export const occurrenceEngagementStore = {
  /** Clears everything when the signed-in token changes (silent; render-safe). */
  ensureSession(token) {
    if (token === sessionToken) return;
    sessionToken = token;
    entries = {};
    pendingLikes = new Set();
    pendingReactions = new Set();
  },

  /** A point to pass to `seed` for a snapshot requested from now on. */
  mark() {
    return seq;
  },

  /** Applies a server snapshot of one occurrence's engagement unless the
   *  entry changed after `since` (the snapshot's request started earlier). */
  seed(id, engagement, since) {
    const current = entries[id];
    if (current && current.seq > since) return;
    entries = { ...entries, [id]: { engagement: engagement ?? null, revoked: false, seq: since } };
    emit();
  },

  /** Seeds every event-start item of a Home page. */
  seedHomeItems(items, since) {
    (items || []).forEach((item) => {
      if (item.type === "event_start" && item.data) {
        this.seed(item.data.id, item.data.engagement, since);
      }
    });
  },

  /** The viewer can no longer see this occurrence: drop its engagement. */
  revoke(id) {
    write(id, { engagement: null, revoked: true });
  },

  setCommentCount(id, count) {
    patchEngagement(id, { comment_count: count });
  },

  /** Likes (`liked` true) or unlikes, optimistically; the server's state is
   *  applied after. On failure the previous state is restored and the error
   *  rethrown. Ignored while a like request for this occurrence is in flight. */
  async setLiked(id, liked) {
    const before = entries[id]?.engagement;
    if (!before || pendingLikes.has(id)) return;
    pendingLikes = setPending(pendingLikes, id, true);
    const wasLiked = before.is_liked;
    const wasCount = before.like_count;
    if (wasLiked !== liked) {
      patchEngagement(id, {
        is_liked: liked,
        like_count: Math.max(0, wasCount + (liked ? 1 : -1)),
      });
    } else {
      emit();
    }
    try {
      const data = await setOccurrenceLike(id, liked);
      patchEngagement(id, { is_liked: data.is_liked, like_count: data.likes_count });
    } catch (err) {
      patchEngagement(id, { is_liked: wasLiked, like_count: wasCount });
      throw err;
    } finally {
      pendingLikes = setPending(pendingLikes, id, false);
      emit();
    }
  },

  /** Adds, switches or — the viewer's current reaction picked again — removes
   *  their reaction (removal is a DELETE, allowed after cancellation). Not
   *  optimistic, as for posts. Resolves to { my_reaction, reaction_counts }. */
  async react(id, reactionType) {
    const current = entries[id]?.engagement;
    if (!current || pendingReactions.has(id)) return null;
    pendingReactions = setPending(pendingReactions, id, true);
    emit();
    try {
      const data =
        current.my_reaction === reactionType
          ? await removeOccurrenceReaction(id)
          : await reactToOccurrence(id, reactionType);
      const result = {
        my_reaction: data.my_reaction ?? null,
        reaction_counts: data.reaction_counts || {},
      };
      patchEngagement(id, result);
      return result;
    } finally {
      pendingReactions = setPending(pendingReactions, id, false);
      emit();
    }
  },
};

// --- Hooks ---------------------------------------------------------------

/** The occurrence's entry ({ engagement, revoked, seq }), or undefined when
 *  nothing has been seeded for it yet. */
export function useOccurrenceEngagement(id) {
  return useSyncExternalStore(subscribe, () => entries[id]);
}

export function useIsOccurrenceLikePending(id) {
  return useSyncExternalStore(subscribe, () => pendingLikes.has(id));
}

export function useIsOccurrenceReactionPending(id) {
  return useSyncExternalStore(subscribe, () => pendingReactions.has(id));
}
